import { useAutoDemo } from '../lib/auto-demo'

// Demo overlay uses a magenta/black palette deliberately outside the site's
// amber + deep-navy system, so viewers can tell it is the demo controller
// rather than a product surface.
const DEMO_ACCENT = 'oklch(72% 0.22 340)'
const DEMO_ACCENT_DIM = 'oklch(56% 0.18 340)'
const DEMO_BG = 'oklch(15% 0.02 280 / 0.92)'
const DEMO_BORDER = 'oklch(72% 0.22 340 / 0.55)'
const DEMO_DIVIDER = 'oklch(50% 0.05 280 / 0.55)'
const DEMO_TEXT = 'oklch(92% 0.02 280)'
const DEMO_MUTED = 'oklch(75% 0.03 280)'
const DEMO_WARN = 'oklch(80% 0.18 50)'

export function AutoDemoOverlay() {
  const ad = useAutoDemo()
  if (!ad.isActive || !ad.currentStep) return null

  const stepNumber = ad.stepIndex + 1
  const progress = (stepNumber / ad.totalSteps) * 100

  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex flex-col items-center gap-2 pointer-events-none">
      <div
        className="pointer-events-auto flex items-center gap-2.5 rounded-full px-3.5 py-1.5 backdrop-blur-md"
        style={{
          background: DEMO_BG,
          border: `1px solid ${DEMO_BORDER}`,
          boxShadow: `0 8px 32px ${DEMO_ACCENT} / 0.18, 0 0 0 1px ${DEMO_ACCENT} / 0.15 inset`,
        }}
      >
        <span
          className="mono text-[10px] uppercase tracking-[0.18em]"
          style={{ color: DEMO_ACCENT }}
        >
          Auto demo
        </span>
        <span className="mono text-[11px]" style={{ color: DEMO_MUTED }}>
          {String(stepNumber).padStart(2, '0')} / {String(ad.totalSteps).padStart(2, '0')}
        </span>
        <span className="h-4 w-px" style={{ background: DEMO_DIVIDER }} />
        <ControlButton onClick={ad.prev} disabled={ad.stepIndex === 0} ariaLabel="Previous step">
          ⏮
        </ControlButton>
        {ad.isPaused ? (
          <ControlButton onClick={ad.resume} primary ariaLabel="Resume">
            ▶
          </ControlButton>
        ) : (
          <ControlButton onClick={ad.pause} primary ariaLabel="Pause">
            ⏸
          </ControlButton>
        )}
        <ControlButton onClick={ad.next} ariaLabel="Next step">
          ⏭
        </ControlButton>
        <span className="h-4 w-px" style={{ background: DEMO_DIVIDER }} />
        <ControlButton onClick={ad.stop} ariaLabel="Stop">
          ✕
        </ControlButton>
        {ad.audioMissing && (
          <span
            className="mono text-[10px] uppercase tracking-[0.16em] pl-1"
            style={{ color: DEMO_WARN }}
          >
            audio off
          </span>
        )}
      </div>
      <div
        className="pointer-events-auto h-[3px] w-[min(360px,calc(100vw-32px))] rounded-full overflow-hidden"
        style={{ background: DEMO_BG }}
      >
        <div
          className="h-full transition-[width] duration-500 ease-out"
          style={{ width: `${progress}%`, background: DEMO_ACCENT }}
        />
      </div>
    </div>
  )
}

interface ControlButtonProps {
  children: React.ReactNode
  onClick: () => void
  primary?: boolean
  disabled?: boolean
  ariaLabel: string
}

function ControlButton({ children, onClick, primary, disabled, ariaLabel }: ControlButtonProps) {
  const base =
    'flex items-center justify-center rounded-full mono text-[12px] transition-colors duration-150 disabled:opacity-30 disabled:cursor-not-allowed'
  const size = primary ? 'w-8 h-8' : 'w-6 h-6'
  const style: React.CSSProperties = primary
    ? { background: DEMO_ACCENT, color: 'oklch(15% 0.02 280)' }
    : { color: DEMO_TEXT, background: 'transparent' }
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      className={`${base} ${size}`}
      style={style}
      onMouseEnter={(e) => {
        if (disabled) return
        if (primary) {
          ;(e.currentTarget as HTMLButtonElement).style.background = DEMO_ACCENT_DIM
        } else {
          ;(e.currentTarget as HTMLButtonElement).style.background = 'oklch(30% 0.05 280 / 0.6)'
        }
      }}
      onMouseLeave={(e) => {
        if (primary) {
          ;(e.currentTarget as HTMLButtonElement).style.background = DEMO_ACCENT
        } else {
          ;(e.currentTarget as HTMLButtonElement).style.background = 'transparent'
        }
      }}
    >
      {children}
    </button>
  )
}
